import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Play, CirclePause, Search, Calendar, MapPin, Clock } from 'lucide-react';
import { PrimaryButton, LargePrimaryButton, LargeDangerButton, DefaultButton } from '../Common/BaseButton';
import { getEventsByUser, searchUserEvents } from '../../services/supabase';
import { formatDateForDisplay, formatTimeForDisplay } from '../../utils/utils';

const FormContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    border-color: #2D882D;
    outline: none;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    border-color: #2D882D;
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    border-color: var(--primary-green-dark);
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  color: #2D882D;
  margin-top: 0.5rem;
`;

const ClockControls = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const RunningClock = styled.div`
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const RunningTime = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const RunningInfo = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const LeaveWarning = styled.div`
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const TimeInputsRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const HalfWidthFormGroup = styled(FormGroup)`
  flex: 1;
  margin-bottom: 0; // Remove extra margin since the row will handle spacing
`;

const formatDuration = (milliseconds) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const calculateDuration = (start, end) => {
  if (!start || !end) return 0;
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
};

function toLocalDatetimeInputValue(date) {
  if (!date) return '';
  const pad = n => n.toString().padStart(2, '0');
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes())
  );
}

const EventSelectorContainer = styled.div`
  position: relative;
`;

const EventSearchInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  padding-left: 2.5rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    border-color: var(--primary-green);
    outline: none;
    box-shadow: 0 0 0 3px var(--primary-green-light);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  width: 16px;
  height: 16px;
`;

const EventDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  overflow-y: auto;
  margin-top: 4px;
`;

const EventOption = styled.div`
  padding: 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: transform 0.2s ease, background-color 0.2s ease;
  will-change: transform, background-color;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &.selected {
    background-color: var(--primary-green-light);
    border-left: 3px solid var(--primary-green);
  }
`;

const EventTitle = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
  font-size: 0.95rem;
`;

const EventMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.25rem;
  font-size: 0.8rem;
  color: #666;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const EventMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const EventLocation = styled.div`
  font-size: 0.8rem;
  color: #888;
  margin-top: 0.25rem;
`;

const SelectedEventDisplay = styled.div`
  background-color: var(--primary-green-light);
  border: 1px solid var(--primary-green);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SelectedEventInfo = styled.div`
  flex: 1;
`;

const ClearEventButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-green);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: transform 0.2s ease, background-color 0.2s ease;
  will-change: transform;
  
  &:hover {
    background-color: rgba(22, 163, 74, 0.1);
  }
`;

const NoEventsMessage = styled.div`
  padding: 1rem;
  text-align: center;
  color: #666;
  font-size: 0.9rem;
`;

const EventSectionHeader = styled.div`
  padding: 0.75rem 1rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
`;

const SearchPrompt = styled.div`
  padding: 0.75rem 1rem;
  font-size: 0.8rem;
  color: var(--primary-green);
  background-color: var(--primary-green-light);
  border-top: 1px solid #eee;
  text-align: center;
  font-weight: 500;
`;

const SearchLoadingMessage = styled.div`
  padding: 1rem;
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  font-style: italic;
`;

const TimeEntryForm = ({ userId, onSubmit, loading, error, successMessage }) => {
  const STORAGE_KEY = `timeEntry_${userId}`;
  
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [eventId, setEventId] = useState('');
  const [userEvents, setUserEvents] = useState([]); // Recent/upcoming events
  const [searchResults, setSearchResults] = useState([]); // Search results
  const [eventCounts, setEventCounts] = useState({ upcoming: 0, total: 0 }); // Just the counts
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [errorForm, setErrorForm] = useState(null);
  
  // Event selector state
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [wasClockingInWhenLeft, setWasClockingInWhenLeft] = useState(false);
  const [hasUnsavedSession, setHasUnsavedSession] = useState(false);
  
  // Load only upcoming events + counts on mount
  useEffect(() => {
    const loadInitialData = async () => {
      if (!userId) return;
      
      setLoadingEvents(true);
      try {
        // Get upcoming events (limited)
        const { data, error } = await getEventsByUser(userId);
        if (error) throw error;
        
        const allEvents = data
          .filter(item => item.event)
          .map(item => item.event);
        
        // Filter upcoming events
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const upcomingEvents = allEvents
          .filter(event => {
            const eventDate = new Date(event.event_date);
            const endTime = new Date(event.end_time);
            return eventDate >= today || endTime >= now;
          })
          .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
          .slice(0, 5);
        
        setUserEvents(upcomingEvents);
        
        // Set counts for display
        setEventCounts({
          upcoming: upcomingEvents.length,
          total: allEvents.length
        });
        
      } catch (err) {
        console.error('Error loading user events:', err);
      } finally {
        setLoadingEvents(false);
      }
    };

    loadInitialData();
  }, [userId]);

  // Debounced search function
  useEffect(() => {
    const searchEvents = async () => {
      if (!eventSearchTerm.trim() || !userId) {
        setSearchResults([]);
        return;
      }

      setSearchLoading(true);
      try {
        const { data, error } = await searchUserEvents(userId, eventSearchTerm.trim());
        if (error) throw error;
        
        setSearchResults(data || []);
      } catch (err) {
        console.error('Error searching events:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    };

    const timeoutId = setTimeout(searchEvents, 300);
    return () => clearTimeout(timeoutId);
  }, [eventSearchTerm, userId]);

  const filteredEvents = eventSearchTerm.trim() ? searchResults : userEvents;

  // Handle event selection
  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setEventId(event.id);
    setEventSearchTerm('');
    setShowEventDropdown(false);
  };

  // Clear selected event
  const handleClearEvent = () => {
    setSelectedEvent(null);
    setEventId('');
    setEventSearchTerm('');
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    setShowEventDropdown(true);
  };

  // Handle search input blur (with delay to allow clicks)
  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowEventDropdown(false);
    }, 200);
  };

  // Update saved state to include eventId
  useEffect(() => {
    const loadSavedState = () => {
      try {
        const savedData = localStorage.getItem(STORAGE_KEY);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          
          if (parsedData.isClockedIn) {
            const savedClockInTime = new Date(parsedData.clockInTime);
            setStartTime(savedClockInTime);
            setClockInTime(savedClockInTime);
            setIsClockedIn(true);
            
            const now = new Date();
            setElapsedTime(now - savedClockInTime);
            
            if (parsedData.wasClockingInWhenLeft) {
              setWasClockingInWhenLeft(true);
            }
          }

          if (parsedData.startTime) setStartTime(new Date(parsedData.startTime));
          if (parsedData.endTime) setEndTime(new Date(parsedData.endTime));
          if (parsedData.description) setDescription(parsedData.description);
          if (parsedData.category) setCategory(parsedData.category);
          if (parsedData.location) setLocation(parsedData.location);
          if (parsedData.eventId) setEventId(parsedData.eventId);
          
          // Restore selected event
          if (parsedData.selectedEvent) {
            setSelectedEvent(parsedData.selectedEvent);
          }
        }
      } catch (err) {
        console.error('Error loading saved time entry state:', err);
      }
    };
    
    loadSavedState();
  }, [STORAGE_KEY]);

  useEffect(() => {
    let interval = null;
    
    if (isClockedIn && clockInTime) {
      interval = setInterval(() => {
        const now = new Date();
        setElapsedTime(now - clockInTime);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    
    return () => clearInterval(interval);
  }, [isClockedIn, clockInTime]);
  
  useEffect(() => {
    if (userId) {
      const stateToSave = {
        isClockedIn,
        clockInTime: clockInTime ? clockInTime.toISOString() : null,
        startTime: startTime ? startTime.toISOString() : null,
        endTime: endTime ? endTime.toISOString() : null,
        description,
        category,
        location,
        eventId,
        selectedEvent,
        wasClockingInWhenLeft
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    }
  }, [
    userId,
    isClockedIn,
    clockInTime,
    startTime,
    endTime,
    description,
    category,
    location,
    eventId,
    selectedEvent,
    wasClockingInWhenLeft,
    STORAGE_KEY
  ]);
  
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isClockedIn) {
        const currentData = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        localStorage.setItem(
          STORAGE_KEY, 
          JSON.stringify({...currentData, wasClockingInWhenLeft: true})
        );
        
        const message = 'You are currently clocked in. If you leave, your timer will continue running.';
        e.returnValue = message;
        return message;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isClockedIn, STORAGE_KEY]);

  const handleClockIn = () => {
    if (hasUnsavedSession) {
      if (!window.confirm("You have an unsaved time entry. Please save or discard it before starting a new time entry.")) {
        return;
      }
      // resetForm();
      return;
    }
    const now = new Date();
    setIsClockedIn(true);
    setClockInTime(now);
    setStartTime(now);
    setEndTime(null);
    setHasUnsavedSession(false);
  };

  const handleClockOut = () => {
    if (!isClockedIn) return;
    const now = new Date();
    setIsClockedIn(false);
    setEndTime(now);
    setHasUnsavedSession(true);
  };
  
  const handleFormInputChange = (field, value) => {
    switch(field) {
      case 'description':
        setDescription(value);
        break;
      case 'category':
        setCategory(value);
        break;
      case 'location':
        setLocation(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!startTime || !endTime) {
      setErrorForm('Please select both start and end times');
      return;
    }
    
    if (startTime >= endTime) {
      setErrorForm('End time must be after start time');
      return;
    }
    
    if (!description.trim()) {
      setErrorForm('Please enter a description of your work');
      return;
    }
    
    const timeData = {
      user_id: userId,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      duration_minutes: calculateDuration(startTime, endTime),
      description: description.trim(),
      category: category.trim() || null,
      location: location.trim() || null,
      event_id: eventId || null,
      selectedEvent: selectedEvent,
    };
    
    await onSubmit(timeData);
    resetForm();
  };
  
  const resetForm = () => {
    setStartTime(null);
    setEndTime(null);
    setDescription('');
    setCategory('');
    setLocation('');
    setEventId('');
    setSelectedEvent(null);
    setEventSearchTerm('');
    setElapsedTime(0);
    setClockInTime(null);
    setIsClockedIn(false);
    setHasUnsavedSession(false);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <FormContainer>
      {wasClockingInWhenLeft && (
        <LeaveWarning>
          You were clocked in when you left the page. The timer has continued running.
        </LeaveWarning>
      )}
      
      <ClockControls>
        <LargePrimaryButton
          type="button"
          onClick={handleClockIn}
          disabled={isClockedIn}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
        >
          <Play size={20} />
          <span>Clock In</span>
        </LargePrimaryButton>
        <LargeDangerButton
          type="button"
          onClick={handleClockOut}
          disabled={!isClockedIn}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
        >
          <CirclePause size={20} />
          <span>Clock Out</span>
        </LargeDangerButton>
      </ClockControls>
      
      {isClockedIn && (
        <RunningClock>
          <RunningTime>{formatDuration(elapsedTime)}</RunningTime>
          <RunningInfo>
            Started at {clockInTime.toLocaleTimeString()} on {clockInTime.toLocaleDateString()}
          </RunningInfo>
        </RunningClock>
      )}
      
      <Form onSubmit={handleSubmit}>
        <TimeInputsRow>
          <HalfWidthFormGroup>
            <Label htmlFor="startTime">Start Time *</Label>
            <Input
              id="startTime"
              type="datetime-local"
              value={toLocalDatetimeInputValue(startTime)}
              onChange={e => setStartTime(e.target.value ? new Date(e.target.value) : null)}
              required
              autoComplete="off"
            />
          </HalfWidthFormGroup>
          <HalfWidthFormGroup>
            <Label htmlFor="endTime">End Time *</Label>
            <Input
              id="endTime"
              type="datetime-local"
              value={toLocalDatetimeInputValue(endTime)}
              min={startTime ? toLocalDatetimeInputValue(startTime) : undefined}
              onChange={e => setEndTime(e.target.value ? new Date(e.target.value) : null)}
              required
              autoComplete="off"
            />
          </HalfWidthFormGroup>
        </TimeInputsRow>
        
        {startTime && endTime && (
          <FormGroup>
            <Label>Duration</Label>
            <p>{Math.floor(calculateDuration(startTime, endTime) / 60)} hours {calculateDuration(startTime, endTime) % 60} minutes</p>
          </FormGroup>
        )}


<FormGroup>
          <Label htmlFor="eventId">Event</Label>
          
          {selectedEvent ? (
            <SelectedEventDisplay>
              <SelectedEventInfo>
                <EventTitle>{selectedEvent.title}</EventTitle>
                <EventMeta>
                  <EventMetaItem>
                    <Calendar size={12} />
                    <span>{formatDateForDisplay(selectedEvent.event_date, selectedEvent.timezone)}</span>
                  </EventMetaItem>
                  <EventMetaItem>
                    <Clock size={12} />
                    <span>{formatTimeForDisplay(selectedEvent.start_time, selectedEvent.timezone)}</span>
                  </EventMetaItem>
                </EventMeta>
                {selectedEvent.location && (
                  <EventLocation>
                    <MapPin size={12} style={{ display: 'inline', marginRight: '0.25rem' }} />
                    {selectedEvent.location}
                  </EventLocation>
                )}
              </SelectedEventInfo>
              <ClearEventButton onClick={handleClearEvent}>
                Clear
              </ClearEventButton>
            </SelectedEventDisplay>
          ) : (
            <EventSelectorContainer>
              <SearchIcon />
              <EventSearchInput
                placeholder={loadingEvents ? 'Loading events...' : eventSearchTerm ? 'Search all events...' : 'Search events or select from recent...'}
                value={eventSearchTerm}
                onChange={(e) => setEventSearchTerm(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                disabled={loadingEvents}
              />
              
              {showEventDropdown && (
                <EventDropdown>
                  {!eventSearchTerm && userEvents.length > 0 && (
                    <EventSectionHeader>
                      Upcoming Events ({eventCounts.upcoming} of {eventCounts.total} total)
                    </EventSectionHeader>
                  )}
                  
                  {searchLoading && eventSearchTerm && (
                    <SearchLoadingMessage>Searching events...</SearchLoadingMessage>
                  )}
                  
                  {!searchLoading && filteredEvents.length === 0 ? (
                    <NoEventsMessage>
                      {eventSearchTerm ? 'No events match your search' : 'No upcoming events found'}
                    </NoEventsMessage>
                  ) : (
                    <>
                      {filteredEvents.map((event) => (
                        <EventOption key={event.id} onClick={() => handleEventSelect(event)}>
                          <EventTitle>{event.title}</EventTitle>
                          <EventMeta>
                            <EventMetaItem>
                              <Calendar size={12} />
                              <span>{formatDateForDisplay(event.event_date, event.timezone)}</span>
                            </EventMetaItem>
                            <EventMetaItem>
                              <Clock size={12} />
                              <span>{formatTimeForDisplay(event.start_time, event.timezone)}</span>
                            </EventMetaItem>
                          </EventMeta>
                          {event.location && (
                            <EventLocation>
                              <MapPin size={12} style={{ display: 'inline', marginRight: '0.25rem' }} />
                              {event.location}
                            </EventLocation>
                          )}
                        </EventOption>
                      ))}
                      {/* Limit the upcoming events to 5 */}
                      {!eventSearchTerm && eventCounts.total > 5 && ( 
                        <SearchPrompt>
                          Type to search through all {eventCounts.total} events assigned to you...
                        </SearchPrompt>
                      )}
                    </>
                  )}
                </EventDropdown>
              )}
            </EventSelectorContainer>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="category">Category *</Label>
          <Select
            id="category"
            value={category}
            onChange={(e) => handleFormInputChange('category', e.target.value)}
            required
          >
            <option value="">Select a category</option>
            <option value="Pre-Event">Pre-Event</option>
            <option value="During Event">During Event</option>
            <option value="Post-Event">Post-Event</option>
            <option value="Miscellaneous">Miscellaneous</option>
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="description">Description *</Label>
          <TextArea
            id="description"
            value={description}
            onChange={(e) => handleFormInputChange('description', e.target.value)}
            placeholder="Describe the work you did"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="location">Location (Optional)</Label>
          <Input
            id="location"
            value={location}
            onChange={(e) => handleFormInputChange('location', e.target.value)}
            placeholder="Where did you work?"
          />
        </FormGroup>
        
        <ButtonContainer>
          <DefaultButton type="button" onClick={resetForm}>
            Clear
          </DefaultButton>
          <PrimaryButton type="submit" disabled={loadingForm || isClockedIn}>
            {loadingForm ? 'Saving...' : 'Save'}
          </PrimaryButton>
          
        </ButtonContainer>
        
        {errorForm && <ErrorMessage>{errorForm}</ErrorMessage>}
        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
      </Form>
    </FormContainer>
  );
};

export default TimeEntryForm; 